import { useState } from "react";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { value: "founder", label: "Founder" },
  { value: "business", label: "Business" },
  { value: "nonprofit", label: "Nonprofit" },
];

const inputCls =
  "w-full border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-faint/70 transition-colors duration-300 focus:border-amber focus:outline-none";

export default function LeadForm({
  source,
  showOrganization = true,
  showNeeds = true,
  submitLabel = "Send",
  successTitle = "Thank you — your message is on its way.",
  successNote = "I'll get back to you shortly. If it's urgent, you can also book an intro call directly.",
  testid,
}) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", audience: "", needs: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organization: form.organization || null,
          audience: form.audience || null,
          needs: form.needs || null,
          source,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
    } catch {
      setStatus("error");
      setError("Something went wrong sending your message. Please try again, or email kpstrategyteam@gmail.com directly.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid={`${testid}-success`} className="border border-amber/50 bg-amber-soft p-8 sm:p-10">
        <p className="font-serif text-2xl font-normal text-ink">{successTitle}</p>
        <p className="mt-3 text-sm leading-relaxed text-body">{successNote}</p>
      </div>
    );
  }

  return (
    <form data-testid={testid} onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testid}-name`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
            Name *
          </label>
          <input
            id={`${testid}-name`}
            data-testid={`${testid}-name`}
            required
            value={form.name}
            onChange={set("name")}
            className={inputCls}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor={`${testid}-email`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
            Email *
          </label>
          <input
            id={`${testid}-email`}
            data-testid={`${testid}-email`}
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            className={inputCls}
            placeholder="you@example.com"
          />
        </div>
      </div>

      {showOrganization && (
        <div>
          <label htmlFor={`${testid}-organization`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
            Organization
          </label>
          <input
            id={`${testid}-organization`}
            data-testid={`${testid}-organization`}
            value={form.organization}
            onChange={set("organization")}
            className={inputCls}
            placeholder="Company or organization"
          />
        </div>
      )}

      <div>
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
          I am a…
        </p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Audience">
          {AUDIENCES.map((a) => (
            <button
              key={a.value}
              type="button"
              data-testid={`${testid}-audience-${a.value}`}
              onClick={() => setForm({ ...form, audience: form.audience === a.value ? "" : a.value })}
              aria-pressed={form.audience === a.value}
              className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-colors duration-300 ${
                form.audience === a.value
                  ? "border-amber bg-amber text-paper"
                  : "border-line bg-white text-body hover:border-amber/60 hover:text-ink"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {showNeeds && (
        <div>
          <label htmlFor={`${testid}-needs`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-faint">
            What do you need help with? <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <textarea
            id={`${testid}-needs`}
            data-testid={`${testid}-needs`}
            rows={4}
            value={form.needs}
            onChange={set("needs")}
            className={`${inputCls} resize-none`}
            placeholder="A sentence or two is plenty."
          />
        </div>
      )}

      {status === "error" && (
        <p data-testid={`${testid}-error`} className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        data-testid={`${testid}-submit`}
        type="submit"
        disabled={status === "sending"}
        className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold tracking-wide text-paper transition-colors duration-300 hover:bg-amber disabled:opacity-60"
      >
        {status === "sending" ? "Sending…" : submitLabel}
        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </form>
  );
}
